import React, { useMemo, useState } from 'react';
import { Box, Typography, Button, Modal, Paper, TextField } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { feeConfig } from '../../../utils/constants/fees';
import { calculateBuyerFee, calculateTaxForCostBreakdown } from "../../../utils/methods/generalVehicleMethods"
import CommaSeparatedInput from '../CustomInputs/CommaSeparatedInput';
import { USER_ROLES } from '../../../utils/constants/userRoles';
import { walletTypes } from '../../../utils/constants/walletTypes';
import { useCalculateBuyerPaymentCryptoMutation } from '../../../toolkit/payments/paymentsApi';
import { debounce } from 'lodash';
import { currencies, currentCurrency } from '../../../utils/constants/currencies';

// --- Helper Component for a single cost item row ---

const CostRow = ({ 
  label, amount, isTotal = false, isHeader = false,
  currencyIcon
}) => (
  <Box
    component="div"
    sx={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      // All units in rem
      py: isHeader ? '0.5rem' : '0.75rem',
      borderBottom: isTotal ? '0.125rem solid' : '0.0625rem solid',
      borderColor: isTotal ? 'primary.main' : 'grey.300',
      fontWeight: isTotal ? 700 : (isHeader ? 600 : 400),
      fontSize: isTotal ? '1.1rem' : '1rem',
      color: isTotal ? 'primary.main' : 'text.primary',
      '&:last-of-type': {
        borderBottom: isTotal ? '0.125rem solid' : 'none',
      }
    }}
  >
    <Typography
      variant="body1"
      component="span"
      sx={{
        fontWeight: isTotal ? 700 : (isHeader ? 600 : 400),
        fontSize: 'inherit',
        color: isHeader ? 'text.secondary' : 'inherit',
      }}
    >
      {label}
    </Typography>
    <Typography
      variant="body1"
      component="span"
      sx={{
        fontWeight: isTotal ? 700 : (isHeader ? 600 : 400),
        fontSize: 'inherit',
        color: 'inherit',
      }}
    >
      {/* Formats amount to currency string: $XX,XXX.XX */}
      {amount !== null ? `${currencyIcon || "$"} ${amount.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}` : 'N/A'}
    </Typography>
  </Box>
);

// --- Internal Component: Content for the Modal (The breakdown logic) ---

const BuyerFeesContent = ({ 
  purchasePrice, 
  setPurchasePrice,
  ownerRole,
  walletType,
  Vehicle_Id
}) => {
  const { t } = useTranslation('liveAuction');

  const bidAmount = parseFloat(purchasePrice) || 0;
  
  // Calculated Fees
  const buyerFee = useMemo(() => calculateBuyerFee(bidAmount), [bidAmount]);

  const [calculateBuyerPaymentApiHandler, calculateBuyerPaymentApiData] = useCalculateBuyerPaymentCryptoMutation();

  // Fixed Fees using the local feeConfig (only serviceFee is used in calculation)
  const serviceFee = feeConfig.service_fee || 98.00;
  
  // Subtotal and Tax Calculation
  const subtotal = useMemo(() =>
    bidAmount + buyerFee + serviceFee,
    [bidAmount, buyerFee, serviceFee]
  );

  // const salesTax = useMemo(() => {
  //   // Use tax_rate property
  //   return subtotal * ((feeConfig.tax_rate || 0) / 100);
  // }, [subtotal]);
  const salesTax = calculateTaxForCostBreakdown(
    bidAmount,
    calculateBuyerFee(bidAmount), // buyerFee
    serviceFee
  );

  // CORRECTED: totalCost should be subtotal + salesTax (as subtotal already includes the bid amount and fees)
  const totalCost = useMemo(() => {
    if (ownerRole === USER_ROLES.seller) {
      return subtotal;
    } else {
      return subtotal + salesTax;
    }
  }, [subtotal, salesTax]);

  // Data for Rendering - Matches the structure from the original component
  const breakdownItems = [
    { label: t('Bid Amount'), amount: bidAmount },
    { label: t('Buyer Fee'), amount: buyerFee },
    { label: t('Service Fee'), amount: serviceFee },
    // { label: 'Sales Tax', amount: salesTax },
  ];

  if (ownerRole === USER_ROLES.dealer) {
    breakdownItems.push(
      { label: t('Sales Tax'), amount: salesTax },
    );
  }
  
  const handlePriceChange = (event) => {
    // Allow only numerical input (or empty string)
    if (parseInt(event?.target?.value) <= 10 ** 9) {
      setPurchasePrice(event?.target?.value);
    }
  };

  const debouncedApiCall = React.useMemo(() => {
    // The actual function that will be debounced
    const callApi = async (cost, id) => {
      // Check dependencies inside the debounced function's execution
      if (!id) {
        return;
      }
      
      const res = await calculateBuyerPaymentApiHandler({
        payload: {
          amount: cost,
          vehicleId: id
        }
      });
      // Handle res here...
    };

    // Return the debounced version, created only once on mount
    // Delay set to 500ms for example
    return debounce(callApi, 500); 
  }, []); // Empty dependency array: debouncedApiCall is stable across renders


  React.useEffect(() => {

    if (walletType !== walletTypes.crypto || !purchasePrice) {
      return;
    }

    debouncedApiCall(purchasePrice, Vehicle_Id);

    // 3. Cleanup: Return a function to cancel any pending calls
    return () => {
      debouncedApiCall.cancel();
    };
  }, [
    walletType,
    purchasePrice, 
    Vehicle_Id, 
    debouncedApiCall
  ]);


  console.log(walletType, "walletType");

  return (
    <Box
      sx={{
        boxSizing: "border-box",
        width: "100%",
        mx: 'auto',
        p: '1.5rem', // Uniform padding on all sides
        pt: "0",
        borderRadius: '0.5rem',
        boxShadow: 3,
        bgcolor: 'background.paper',
        fontFamily: 'Inter, sans-serif',
      }}
    >
      {/* --- HEADER --- */}
      <Typography
        variant="h6"
        component="h2"
        id="buyer-fees-modal-title"
        sx={{
          fontSize: '1.1rem',
          fontWeight: 700,
          textAlign: "center",
          color: 'text.primary',
          borderBottom: '0.0625rem solid',
          borderColor: 'grey.300',
          py: '1rem',
        }}
      >
        {t('Cost breakdown')}
      </Typography>

      {/* --- INPUT FIELD: Vehicle Purchase Price --- */}
      <CommaSeparatedInput
        fullWidth
        label={t("Vehicle Purchase Amount")}
        variant="outlined"
        value={purchasePrice}
        onChange={handlePriceChange}
        margin="normal"
        type="text"
        placeholder={t("e.g., 25000")}
        inputProps={{ inputMode: 'decimal', pattern: '[0-9]*' }}
        sx={{ mb: '1.5rem' }}
      />

      {/* --- CONDITIONAL BREAKDOWN ITEMS --- */}
      {bidAmount > 0 && (
        <Box component="section" sx={{ mb: '1rem' }} id="buyer-fees-modal-description">
          {breakdownItems.map((item, index) => (
            <CostRow key={index} label={item.label} amount={item.amount} />
          ))}
        </Box>
      )}

      {/* --- CONDITIONAL TOTAL ESTIMATED COST --- */}
      {bidAmount > 0 && (
        <CostRow
          label={t("Total price")}
          amount={totalCost}
          isTotal={true}
        />
      )}

      {bidAmount > 0 && walletType === walletTypes.crypto && calculateBuyerPaymentApiData?.data?.data?.total_payable_in_crypto ? (
        <CostRow
          label={t("Total price(in {{currency}})", { currency: currentCurrency })}
          amount={calculateBuyerPaymentApiData?.data?.data?.total_payable_in_crypto}
          isTotal={true}
          currencyIcon={currentCurrency}
        />
      ) : null}
      
      {/* Show a placeholder if bid is zero */}
      {bidAmount <= 0 && (
        <Typography 
          variant="body2" 
          color="text.primary" 
          textAlign="center" 
          sx={{ py: 1 }}
        >
          {t('Enter a purchase amount above $0 to see the full breakdown.')}
        </Typography>
      )}
    </Box>
  );
};


// --- Main Exported Component: Button and Modal Wrapper (Unchanged) ---

function CostBreakdown({
  selectedCar,
  ownerRole,
  walletType
}) {
  const { t } = useTranslation('liveAuction');
  
  const [open, setOpen] = useState(false);
  // State for the input field is managed here
  const [purchasePrice, setPurchasePrice] = useState(''); 
  
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  // Styles for the modal box positioning
  const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    // width: { xs: '90%', sm: 400 }, // Responsive width
    maxWidth: "700px",
    bgcolor: 'background.paper',
    borderRadius: '0.5rem',
    boxShadow: 24,
    maxHeight: '80vh',
    overflowY: 'auto',
  };

  console.log(walletType, "walletType")

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center"
      }}
    >
      {/* 1. Button to show the calculator */}
      <Button 
        variant="contained" 
        onClick={handleOpen} 
        sx={{ 
          textTransform: 'none', 
          fontWeight: 600,
        }}
      >
        {t('Cost breakdown')}
      </Button>

      {/* 2. Modal containing the fees breakdown */}
      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="buyer-fees-modal-title"
        aria-describedby="buyer-fees-modal-description"
      >
        {/* Use Paper for a nice, elevated look inside the Modal */}
        <Paper sx={modalStyle}>
          {/* Pass the state and setter down to the content component */}
          <BuyerFeesContent 
            purchasePrice={purchasePrice} 
            setPurchasePrice={setPurchasePrice}
            ownerRole={ownerRole}
            walletType={walletType}
            Vehicle_Id={selectedCar?.Vehicle_Id}
          /> 
        </Paper>
      </Modal>
    </Box>
  );
}

export default CostBreakdown;